import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import hoistNonReactStatic from 'hoist-non-react-statics';
import {
  load as loadFacility,
  loadList as loadAllFacilities,
  findId as findFacilityId,
} from 'store/modules/facilities';
import Field from '../../../../../modules/react/components/stateful/Field';
import SelectInput from '../../../../../modules/react/components/stateful/SelectInput';
import widgetDecorator from './widget';
import editModal from './editModal';
import contextMenu from './contextMenu';

export const propKey = '@@facilityWidget';

/**
 * A decorator/HOC used to describe a facility widget component. This HOC will
 * wrap the normal widget HOC and also provide some facility auto-looking magic
 * to provide the `facilityId` and some other facility details as properties to
 * your component.
 *
 * These options are the same accepted by the {@link widgetDecorator} decorator/HOC,
 * so check that documentation for more information.
 */
export default (details, { resolve = null, ...options } = {}) => {
  const createHoc = (Component) => {
    // Wrap the resolve function
    const wrappedResolve = function ({ store, ...widget }) {
      const facilityId = findFacilityId(widget.payload || {}, store.getState());
      let actions = [];

      // If we have a facility ID, attempt to resolve this widget
      if (facilityId) {
        if (resolve) {
          actions = resolve({ store, ...widget, facilityId });
          if (!actions) {
            actions = []; // There's a chance this resolve returned nothing
          } else if (!Array.isArray(actions)) {
            actions = [actions];
          }
        }

        actions.push(loadFacility(facilityId));
      } else {
        // If we don't have a facility ID yet, load all of the facilities so we can build a selector
        actions.push(loadAllFacilities());
      }

      return actions;
    };

    // Apply the widget decorator to the passed component
    const WidgetComponent = widgetDecorator(details, { ...options, resolve: wrappedResolve })(Component);

    // This component will act as a placeholder in the event that the widget does not have a facility ID
    const PlaceholderWidget = () => (
      <div>
        Please set the facility
      </div>
    );
    const PlaceholderFacilityWidget = editModal(
      {},
      ({ facilities }) => {
        const availableFacilities = {};
        facilities.forEach(({ id, name }) => {
          availableFacilities[id] = name;
        });

        return [{
          children: (
            <Field
              component={SelectInput}
              htmlSelect
              label="Facility"
              name="facilityId"
              autoFocus
              required
              emptyOption="Select a Facility"
              options={availableFacilities}
            />
          ),
        }];
      },
    )(contextMenu()(hoistNonReactStatic(PlaceholderWidget, WidgetComponent)));

    // This wrapper component will show the placeholder component if no facility ID can be determined
    const FacilityWidget = (props) => {
      if (!props.facilityId) {
        return <PlaceholderFacilityWidget {...props} />;
      }

      return <WidgetComponent {...props} />;
    };

    FacilityWidget.propTypes = {
      facilityId: PropTypes.string,
    };

    FacilityWidget.defaultProps = {
      facilityId: null,
    };

    FacilityWidget.displayName = `FacilityWidget(${WidgetComponent.displayName})`;

    // Automatically inject the facility details from the state into this widget
    const mapStateToProps = (state, props) => {
      const facilityId = findFacilityId(props, state);
      return {
        facilityId,
        facilities: state.facilities.allFacilities,
        facility: state.facilities.info[facilityId],
        unitGroups: state.facilities.unitGroups[facilityId],
        unitSummary: state.facilities.unitSummary[facilityId],
      };
    };
    return connect(mapStateToProps)(hoistNonReactStatic(FacilityWidget, WidgetComponent));
  };

  if (process.env.NODE_ENV === 'testing') {
    createHoc.$$voyagerTestWhitelist = true;
  }

  return createHoc;
};
